import { Injectable, InjectionToken, NgZone, OnDestroy, Signal, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Capacitor, PluginListenerHandle, SystemBarType, SystemBars, SystemBarsStyle } from '@capacitor/core';
import { OrientationLockType, ScreenOrientation } from '@capacitor/screen-orientation';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, Subscription, combineLatest, debounceTime, first, map, skip } from 'rxjs';
import { onZone } from './core';
import { electronBridge } from './electron-bridge';
import { HgmDarkMode } from './hgm-dark-mode';
import { LOGGER, Logger } from './logger';
import { MediaQueryManager } from './media-query-manager';
import { MirrorContextService } from './mirror-context.service';
import { PersistentStore } from './persistent-store';
import { ConnectionManager } from '../connection/connection-manager';
import { isTargetTypeFile } from '../connection/target-device-info';
import { ValueNudgerAccelConfig } from '../param/util/value-nudger';

const gaugeWidthKey = 'uiOptionsGaugeWidth';
const themeKey = 'uiOptionsTheme';
const nudgerMaxSpeedKey = 'uiOptionsNudgerMaxSpeed';
const nudgerExpAccelKey = 'uiOptionsNudgerExpAccel';
const nudgerLinAccelKey = 'uiOptionsNudgerLinAccel';
const navTreeExpandedKey = 'uiOptionsNavTreeExpanded';

export interface UiThemeAuto {
    klassLight: string;
    klassDark: string;
    key: string;
    name: string;
}

export interface UiThemeManual {
    klass: string;
    key: string;
    name: string;
}

export type UiTheme = UiThemeAuto | UiThemeManual;

export type NavTreeExpandedEntry = [string, boolean];

export const UI_THEMES = new InjectionToken<UiTheme[]>('theme names/classes');

const defaultNudgerMaxSpeed = 10;
const defaultNudgerExpAccel = 0.1;
const defaultNudgerLinAccel = 0;
const defaultNavTreeExpanded: NavTreeExpandedEntry[] = [['', true]];

const isNavTreeExpandedEntry = (v: unknown): v is NavTreeExpandedEntry =>
    Array.isArray(v) && v.length === 2 && typeof v[0] === 'string' && typeof v[1] === 'boolean';

const validateNavTreeExpanded = (v: unknown): [string, boolean][] | undefined =>
    Array.isArray(v) && v.every(isNavTreeExpandedEntry) ? v : undefined;

@Injectable({ providedIn: 'root' })
export class UiOptions implements OnDestroy {
    static defaultGaugeWidth = 160;

    themes: UiTheme[] = [{ name: 'Default', key: 'theme-default', klass: 'theme-default' }];
    majorVersion = 0;

    readonly orientation: Signal<OrientationLockType>;
    readonly isPhone: Signal<boolean>;
    readonly isMobile = computed(() => this.platform.is('mobile'));
    readonly isNarrowScreen: Signal<boolean>;
    readonly isElectron = computed(() => !!electronBridge);
    _isAndroid = signal(false);
    readonly isAndroid = this._isAndroid.asReadonly();
    _isIos = signal(true);
    readonly isIos = this._isIos.asReadonly();

    readonly toolbarColor = computed(() => {
        // const targetIsFile = this.connMgr ? isTargetTypeFile(this.connMgr.deviceSignal()?.type) : false;
        // if (targetIsFile || this.mirrorCtx.isFileSignal()) return 'medium';
        return 'primary';
    });

    private _orientation = signal<OrientationLockType>('portrait-primary');
    private screenOrientationListenerHandle: PluginListenerHandle | null = null;

    private gaugeWidthSubj = new BehaviorSubject(UiOptions.defaultGaugeWidth);
    private themeSubj: BehaviorSubject<string>;
    private prefersDarkSubj = new BehaviorSubject(false);
    private prefersDarkSubscription: Subscription | undefined;
    private nudgerMaxSpeedSubj = new BehaviorSubject(defaultNudgerMaxSpeed);
    private nudgerExpAccelSubj = new BehaviorSubject(defaultNudgerExpAccel);
    private nudgerLinAccelSubj = new BehaviorSubject(defaultNudgerLinAccel);
    private xcpTimingOverlaySubj = new BehaviorSubject(false); // does not persist
    private navTreeExpandedSubj = new BehaviorSubject<[string, boolean][]>(defaultNavTreeExpanded);
    private searchInSetupPageSubj = new BehaviorSubject(true);

    // maintains local BehaviorSubject of gaugeWidth, updated immediately by setter
    // after 1 second idle, store value is updated, which will bounce back to local subject

    private readonly logger = inject<Logger>(LOGGER);
    private readonly ngZone = inject(NgZone);
    private readonly store = inject(PersistentStore);
    private readonly platform = inject(Platform);
    private readonly mirrorCtx = inject(MirrorContextService);
    private readonly connMgr = inject(ConnectionManager, { optional: true });

    constructor() {
        const themes = inject(UI_THEMES, { optional: true });
        const querulator = inject(MediaQueryManager);
        if (themes) {
            this.themes = themes;
        }
        this.themeSubj = new BehaviorSubject(this.themes[0].key);
        this.setupTheme(this.themes[0]);

        this.isPhone = computed(() => !electronBridge && !this.platform.is('tablet'));
        this.isNarrowScreen = toSignal(querulator.getQuery('(max-width: 768px)').obs, { requireSync: true });
        this.orientation = this._orientation.asReadonly();

        (async () => {
            await this.platform.ready();
            this._isAndroid.set(this.platform.is('android'));
            this._isIos.set(this.platform.is('ios'));
            if (this.isAndroid() || this.isIos()) {
                this.enableScreenOrientationListener();
            }
            const subscribeToPrefersColorScheme = () => {
                this.prefersDarkSubj.pipe(skip(1), first(), onZone(this.ngZone)).subscribe(() => this.setupTheme(this.themes[0]));
                querulator.getQuery('(prefers-color-scheme: dark)').obs.pipe(onZone(this.ngZone)).subscribe(this.prefersDarkSubj);
            };
            if (this.isAndroid()) {
                // Android WebView doesn't reactively re-evaluate prefers-color-scheme on system theme change,
                // so we bridge via HgmDarkMode (in-app Capacitor plugin). If a host app hasn't registered the
                // plugin natively, fall back to prefers-color-scheme so the app still functions (initial value
                // only — no live updates on system theme toggle until the host adds the plugin).
                HgmDarkMode.isDarkMode().then(
                    ({ dark }) => {
                        this.prefersDarkSubj.next(dark);
                        HgmDarkMode.addListener('darkModeChange', ({ dark }) => this.prefersDarkSubj.next(dark));
                    },
                    (err) => {
                        this.logger.warn('HgmDarkMode plugin not registered natively; falling back to prefers-color-scheme', err);
                        subscribeToPrefersColorScheme();
                    },
                );
            } else {
                subscribeToPrefersColorScheme();
            }
            (await this.store.getItem(gaugeWidthKey)).subscribe({
                next: (value) => {
                    this.gaugeWidth = Number(value) || UiOptions.defaultGaugeWidth;
                    this.gaugeWidthObs.pipe(debounceTime(1000)).subscribe({
                        next: async (width) => {
                            await this.store.setItem(gaugeWidthKey, width);
                        },
                    });
                },
            });
            (await this.store.getItem(themeKey)).subscribe({
                next: (value) => {
                    this.theme = (this.themes.find((theme) => theme.key === value) ?? this.themes[0]).key;
                    this.themeObs.subscribe({
                        next: async (theme) => {
                            await this.store.setItem(themeKey, theme);
                        },
                    });
                },
            });
            (await this.store.getItem(nudgerMaxSpeedKey)).subscribe({
                next: (value) => {
                    this.nudgerMaxSpeed = Number(value) || defaultNudgerMaxSpeed;
                    this.nudgerMaxSpeedObs.pipe(debounceTime(1000)).subscribe({
                        next: async (v) => {
                            await this.store.setItem(nudgerMaxSpeedKey, v);
                        },
                    });
                },
            });
            (await this.store.getItem(nudgerExpAccelKey)).subscribe({
                next: (value) => {
                    this.nudgerExpAccel = Number(value) || defaultNudgerExpAccel;
                    this.nudgerExpAccelObs.pipe(debounceTime(1000)).subscribe({
                        next: async (v) => {
                            await this.store.setItem(nudgerExpAccelKey, v);
                        },
                    });
                },
            });
            (await this.store.getItem(nudgerLinAccelKey)).subscribe({
                next: (value) => {
                    this.nudgerLinAccel = Number(value) || defaultNudgerLinAccel;
                    this.nudgerLinAccelObs.pipe(debounceTime(1000)).subscribe({
                        next: async (v) => {
                            await this.store.setItem(nudgerLinAccelKey, v);
                        },
                    });
                },
            });
            (await this.store.getItem(navTreeExpandedKey)).subscribe({
                next: (value) => {
                    this.navTreeExpanded = validateNavTreeExpanded(value) || defaultNavTreeExpanded;
                    this.navTreeExpandedObs.pipe(debounceTime(1000)).subscribe({
                        next: async (v) => {
                            await this.store.setItem(navTreeExpandedKey, v);
                        },
                    });
                },
            });
        })();
    }

    get gaugeWidth() {
        return this.gaugeWidthSubj.value;
    }
    set gaugeWidth(value) {
        this.gaugeWidthSubj.next(value);
    }
    get gaugeWidthObs() {
        return this.gaugeWidthSubj.asObservable();
    }

    get theme() {
        return this.themeSubj.value;
    }
    set theme(value) {
        const selected = this.themes.find((t) => t.key === value)!;
        if (value !== this.themeSubj.value && selected) {
            this.setupTheme(selected);
            this.themeSubj.next(value);
        }
    }
    get themeObs() {
        return this.themeSubj.asObservable();
    }

    get nudgerMaxSpeed() {
        return this.nudgerMaxSpeedSubj.value;
    }
    set nudgerMaxSpeed(value) {
        this.nudgerMaxSpeedSubj.next(value);
    }
    get nudgerMaxSpeedObs() {
        return this.nudgerMaxSpeedSubj.asObservable();
    }

    get nudgerExpAccel() {
        return this.nudgerExpAccelSubj.value;
    }
    set nudgerExpAccel(value) {
        this.nudgerExpAccelSubj.next(value);
    }
    get nudgerExpAccelObs() {
        return this.nudgerExpAccelSubj.asObservable();
    }

    get nudgerLinAccel() {
        return this.nudgerLinAccelSubj.value;
    }
    set nudgerLinAccel(value) {
        this.nudgerLinAccelSubj.next(value);
    }
    get nudgerLinAccelObs() {
        return this.nudgerLinAccelSubj.asObservable();
    }

    get nudgerAccelConfig(): ValueNudgerAccelConfig {
        return {
            maxSpeed: this.nudgerMaxSpeed,
            expAccel: this.nudgerExpAccel,
            linAccel: this.nudgerLinAccel,
        };
    }
    get nudgerAccelConfigObs() {
        return combineLatest([this.nudgerMaxSpeedObs, this.nudgerExpAccelObs, this.nudgerLinAccelObs]).pipe(
            map(([maxSpeed, expAccel, linAccel]) => ({ maxSpeed, expAccel, linAccel })),
        );
    }

    get navTreeExpanded() {
        return this.navTreeExpandedSubj.value;
    }
    set navTreeExpanded(v) {
        this.navTreeExpandedSubj.next(v);
    }
    get navTreeExpandedObs() {
        return this.navTreeExpandedSubj.asObservable();
    }

    get xcpTimingOverlay() {
        return this.xcpTimingOverlaySubj.value;
    }
    set xcpTimingOverlay(v) {
        this.xcpTimingOverlaySubj.next(v);
    }
    get xcpTimingOverlayObs() {
        return this.xcpTimingOverlaySubj.asObservable();
    }

    get searchInSetupPage() {
        return this.searchInSetupPageSubj.value;
    }
    set searchInSetupPage(value) {
        this.searchInSetupPageSubj.next(value);
    }
    get searchInSetupPageObs() {
        return this.searchInSetupPageSubj.asObservable();
    }

    hideStatusBar = async () => {
        if (Capacitor.isNativePlatform()) {
            await SystemBars.hide({ bar: SystemBarType.StatusBar });
        }
    };

    setScreenOrientation(orientation: OrientationLockType) {
        this.updateOrientationState(orientation);
    }
    async enableScreenOrientationListener() {
        // Ensure no existing listener before creating new one
        this.removeScreenOrientationListener();

        this.screenOrientationListenerHandle = await ScreenOrientation.addListener('screenOrientationChange', (orientation) => {
            this.updateOrientationState(orientation.type);
        });
    }

    removeScreenOrientationListener() {
        if (this.screenOrientationListenerHandle) {
            this.screenOrientationListenerHandle.remove();
            this.screenOrientationListenerHandle = null;
        }

        // Fallback: remove all listeners (in case of any edge cases)
        ScreenOrientation.removeAllListeners();
    }

    ngOnDestroy() {
        this.removeScreenOrientationListener();
    }

    private updateOrientationState(orientation: OrientationLockType) {
        this._orientation.set(orientation);
        this.updateStatusBar();
    }

    private setupTheme(selected: UiTheme) {
        this.prefersDarkSubscription?.unsubscribe();
        this.prefersDarkSubscription = undefined;
        const appRoot = document.getElementsByTagName('app-root')[0];
        const notSelected = this.themes.filter((t) => t.key !== selected.key);
        for (const theme of notSelected) {
            if ('klass' in theme) {
                appRoot.classList.remove(theme.klass);
            } else {
                appRoot.classList.remove(theme.klassLight);
                appRoot.classList.remove(theme.klassDark);
            }
        }
        this.prefersDarkSubscription = this.prefersDarkSubj.subscribe((prefersDark) => {
            if ('klass' in selected) {
                appRoot.classList.add(selected.klass);
            } else {
                appRoot.classList.add(prefersDark ? selected.klassDark : selected.klassLight);
                appRoot.classList.remove(prefersDark ? selected.klassLight : selected.klassDark);
            }
            this.updateStatusBar();
        });
    }

    private updateStatusBar = async () => {
        try {
            if (this.platform.is('android')) {
                // With edge-to-edge enforced on Android 15+ (we target SDK 36), the status bar is
                // transparent and the ion-toolbar paints behind it, so the toolbar's --ion-color-primary
                // provides the visible color. The old @capacitor/status-bar setBackgroundColor / getInfo
                // calls used the deprecated get/setStatusBarColor APIs (flagged by the Play Console) and
                // are no-ops in edge-to-edge anyway, so we only set the icon tint via SystemBars
                // (@capacitor/core), which uses WindowInsetsController and avoids those deprecated APIs.
                await SystemBars.setStyle({ style: SystemBarsStyle.Dark, bar: SystemBarType.StatusBar });
            }
        } catch (err) {
            this.logger.error(err);
        }
    };
}
